import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../dados/api-client';
import Loading from '../../componentes/loading';
import CHeader from '../../componentes/c-header';
import CFooter from '../../componentes/c-footer';
import { useQuery } from 'react-query';
import ErroData from '../../componentes/erroData';
import { IBlogPost } from '../../dados/interfaces/blog';
import { Helmet } from 'react-helmet-async';
import { IoSearchOutline } from 'react-icons/io5';

const fetchData = async () => {
    const response = await apiClient('/blog', {
        method: 'GET',
    });
    return response;
};

const formatDate = (dateString: string) => {
    if (!dateString) return 'Data não disponível';
    // Convert "2026-02-13 08:38:47" to ISO format "2026-02-13T08:38:47"
    const isoDate = dateString.replace(' ', 'T');
    const date = new Date(isoDate);
    return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
    });
};

export default function Blog() {
    const navigate = useNavigate();
    
    useEffect(() => {
        navigate('/', { replace: true });
    }, [navigate]);

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string>('todas');

    const { data, isLoading, error } = useQuery<Array<IBlogPost>>('blogData', fetchData);

    if (isLoading) return <Loading />;
    if (error) return <ErroData></ErroData>;

    // Get unique categories
    const categories = ['todas', ...Array.from(new Set(data?.map(post => post.categoria) || []))];

    // Filter posts
    const filteredPosts = data?.filter(post => {
        const matchesSearch = post.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
            post.categoria.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = selectedCategory === 'todas' || post.categoria === selectedCategory;
        return matchesSearch && matchesCategory;
    }) || [];

    return (
        <>
            <Helmet>
                <title>Grupo CHB | Blog</title>
                <meta name="description" content="Fique por dentro das últimas novidades e artigos do nosso blog" />
                <meta name="keywords" content="blog, artigos, novidades, notícias" />
                <meta name="og:title" content="Grupo CHB | Blog" />
            </Helmet>
            <CHeader></CHeader>
            <main className="flex min-h-screen w-full flex-col bg-black">
                <section className="flex flex-col w-full">
                    {/* Header Section */}
                    <section className="flex flex-col justify-center items-center gap-10 py-20 bg-blue-700 px-5 xl:px-0">
                        <div className="flex flex-col gap-6 max-w-7xl w-full">
                            <h1 className="text-[35px] md:text-[40px] lg:text-[45px] xl:text-[50px] text-white font-bold">
                                Blog
                            </h1>
                            <p className="text-white text-lg opacity-90">
                                Fique por dentro das últimas novidades e artigos
                            </p>
                        </div>
                    </section>

                    {/* Main Content */}
                    <section className="flex flex-col justify-center items-center gap-10 py-20 bg-black px-5 xl:px-0">
                        <div className="flex flex-col lg:flex-row gap-10 max-w-7xl w-full">
                            {/* Sidebar */}
                            <aside className="w-full lg:w-64 flex-shrink-0">
                                <div className="bg-zinc-900 rounded-xl p-6 sticky top-5">
                                    <h2 className="text-white text-xl font-bold mb-6">Filtros</h2>

                                    {/* Search Bar */}
                                    <div className="mb-6">
                                        <label className="text-white text-sm font-medium mb-2 block">
                                            Pesquisar
                                        </label>
                                        <div className="relative">
                                            <input
                                                type="text"
                                                placeholder="Buscar posts..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                className="w-full bg-zinc-800 text-white rounded-lg px-4 py-3 pl-10 focus:outline-none focus:ring-2 focus:ring-blue-700 transition-all"
                                            />
                                            <IoSearchOutline className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl" />
                                        </div>
                                    </div>

                                    {/* Categories */}
                                    <div>
                                        <h3 className="text-white text-sm font-medium mb-3">Categorias</h3>
                                        <div className="flex flex-col gap-2">
                                            {categories.map((category, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => setSelectedCategory(category)}
                                                    className={`text-left px-4 py-2 rounded-lg transition-all ${selectedCategory === category
                                                        ? 'bg-blue-700 text-white'
                                                        : 'bg-zinc-800 text-gray-300 hover:bg-zinc-700'
                                                        }`}
                                                >
                                                    <span className="capitalize">{category}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Results Count */}
                                    <div className="mt-6 pt-6 border-t border-zinc-800">
                                        <p className="text-gray-400 text-sm">
                                            {filteredPosts.length} {filteredPosts.length === 1 ? 'post encontrado' : 'posts encontrados'}
                                        </p>
                                    </div>
                                </div>
                            </aside>

                            {/* Blog Posts Grid */}
                            <div className="flex-1">
                                {filteredPosts.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-20">
                                        <p className="text-gray-400 text-lg">Nenhum post encontrado</p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
                                        {filteredPosts.map((post, index) => (
                                            <a
                                                key={index}
                                                href={post.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="group cursor-pointer"
                                            >
                                                <article className="bg-zinc-900 rounded-xl overflow-hidden transition-all hover:scale-105 hover:shadow-2xl h-full flex flex-col">
                                                    {/* Thumbnail */}
                                                    <div className="aspect-w-16 aspect-h-9 w-full overflow-hidden">
                                                        <img
                                                            src={post.thumb}
                                                            alt={post.titulo}
                                                            className="w-full h-48 object-cover transition-transform group-hover:scale-110"
                                                        />
                                                    </div>

                                                    {/* Content */}
                                                    <div className="p-6 flex flex-col flex-1">
                                                        {/* Category Badge */}
                                                        <span className="inline-block bg-blue-700 text-white text-xs font-medium px-3 py-1 rounded-full mb-3 w-fit capitalize">
                                                            {post.categoria}
                                                        </span>

                                                        {/* Date */}
                                                        <p className="text-gray-400 text-sm mb-3">
                                                            {formatDate(post.create_at)}
                                                        </p>

                                                        {/* Title */}
                                                        <h3 className="text-white text-xl font-bold mb-3 line-clamp-2 group-hover:text-blue-400 transition-colors">
                                                            {post.titulo}
                                                        </h3>

                                                        {/* Read More Link */}
                                                        <div className="mt-auto">
                                                            <span className="text-blue-400 text-sm font-medium group-hover:underline">
                                                                Ler mais →
                                                            </span>
                                                        </div>
                                                    </div>
                                                </article>
                                            </a>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </section>
                </section>
            </main>
            <CFooter></CFooter>
        </>
    );
}
